import { useCallback, useState } from 'react'
import { Filter } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { FilterBar } from './FilterBar'
import { FilterPropertyPicker } from './FilterPropertyPicker'
import { useQuickFilters } from './useQuickFilters'
import type { FilterFieldKey } from '@/lib/types/filter'
import useProjectFilterStore from '@/store/ProjectFilterStore'
import { EMPTY_FILTERS } from '@/lib/types/filter'
import { groupNumbers } from '@/lib/filters/filterGroups'
import { Button } from '@/components/ui/button'
import { Drawer, DrawerContent, DrawerDescription, DrawerTitle, OpenOnlyTrigger, useDrawerTapGuard } from '@/components/ui/drawer'
import { useIsMobile } from '@/hooks/useMobile'

interface FilterTriggerButtonProps {
  projectId: string
  hiddenFields?: Array<FilterFieldKey>
  /** Filters the user can't remove (a space's own project filter). */
  pinnedFields?: Array<FilterFieldKey>
  scope?: 'project' | 'space'
}

/**
 * Always-visible filter button for the breadcrumb/header area.
 * Shows "Filter" text + count badge when filters are active.
 * Opens property picker to add a new filter — into the last OR group when
 * the view has groups (design 9b).
 */
export function FilterTriggerButton(props: FilterTriggerButtonProps) {
  const isPhone = useIsMobile()
  return isPhone ? <FilterSheet {...props} /> : <FilterMenuButton {...props} />
}

/**
 * Phones (design 36c): the Filter icon opens a sheet with the same pills,
 * "Add filter", quick filters, Clear and Save as view. Filters apply live,
 * as on desktop; Done just closes.
 */
function FilterSheet({ projectId, hiddenFields, pinnedFields, scope = 'project' }: FilterTriggerButtonProps) {
  const { t } = useTranslation('tasks')
  const [open, setOpenState] = useState(false)
  const setOpen = useDrawerTapGuard(setOpenState, open)
  const activeCount = useProjectFilterStore(
    (s) => (s.filters[projectId] ?? EMPTY_FILTERS).filter((f) => f.values.length > 0).length,
  )
  return (
    <>
      <OpenOnlyTrigger onOpen={() => setOpen(true)}>
        <Button
          variant="ghost"
          size="icon"
          aria-label={t('filters.menu.button')}
          className="relative size-10 text-muted-foreground"
        >
          <Filter className="size-5" />
          {activeCount > 0 && (
            <span className="absolute right-1 top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-brand px-1 text-[10px] font-semibold text-brand-foreground">
              {activeCount}
            </span>
          )}
        </Button>
      </OpenOnlyTrigger>
      <Drawer open={open} onOpenChange={setOpen} direction="bottom" repositionInputs={false}>
      <DrawerContent onOpenAutoFocus={(event) => event.preventDefault()}>
        <DrawerTitle className="px-4 pb-1 pt-3 text-[17px] font-semibold">{t('filters.menu.button')}</DrawerTitle>
        <DrawerDescription className="sr-only">{t('filters.menu.button')}</DrawerDescription>
        <div className="min-h-0 flex-1 overflow-y-auto">
          <FilterBar
            variant="sheet"
            projectId={projectId}
            pinnedFields={pinnedFields}
            hiddenFields={hiddenFields}
            scope={scope}
          />
        </div>
        <div className="border-t px-4 py-3">
          <Button className="h-11 w-full" onClick={() => setOpen(false)}>
            {t('filters.done')}
          </Button>
        </div>
      </DrawerContent>
    </Drawer>
    </>
  )
}

function FilterMenuButton({
  projectId,
  hiddenFields,
  scope = 'project',
}: FilterTriggerButtonProps) {
  const filters = useProjectFilterStore(
    (s) => s.filters[projectId] ?? EMPTY_FILTERS,
  )
  const pendingGroup = useProjectFilterStore((s) => !!s.pendingGroup[projectId])
  const addFilter = useProjectFilterStore((s) => s.addFilter)
  const setPendingGroup = useProjectFilterStore((s) => s.setPendingGroup)
  const group = groupNumbers(filters).at(-1)
  const { quickFilters, toggleQuickFilter } = useQuickFilters(projectId, scope, {
    group,
    lockedFields: hiddenFields,
  })

  const handleSelectField = useCallback(
    (fieldKey: FilterFieldKey) => {
      addFilter(projectId, fieldKey, { group, lockedFields: hiddenFields })
    },
    [projectId, addFilter, group, hiddenFields],
  )

  // Pinned fields (space project) aren't known here; a filter with values
  // that isn't hidden is enough to have something to combine.
  const hidden = new Set(hiddenFields ?? [])
  const canCombine =
    !pendingGroup &&
    filters.some((f) => f.values.length > 0 && !hidden.has(f.fieldKey) && f.fieldKey !== 'project')

  return (
    <FilterPropertyPicker
      projectId={projectId}
      scope={scope}
      lockedFields={hiddenFields}
      activeFilters={filters}
      onSelectField={handleSelectField}
      hasActiveFilters={false}
      hiddenFields={hiddenFields}
      quickFilters={quickFilters}
      onQuickFilter={toggleQuickFilter}
      targetGroup={group}
      onCombineWithOr={canCombine ? () => setPendingGroup(projectId, true) : undefined}
    />
  )
}
